import { CarouselItem } from '@/components/ui/carousel'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useUser } from '@clerk/clerk-react'
import { useTranslation } from 'react-i18next'
import { useNeedsAnalysis } from '@/contexts/needs-analysis/NeedsAnalysisContext'
import type { NativeLanguage } from '@/types/learner-portal'
import { NATIVE_LANGUAGES, NATIVE_LANGUAGE_LABELS, LANGUAGE_TO_LOCALE } from '@/constants/needsAnalysis'
import { I18NEXT_LANGUAGE_STORAGE_KEY } from '@/constants/languages'
import { useAuthToken } from '@/hooks/useAuthToken'
import { meService } from '@/services/meService'

export default function PreferredLanguageScreen() {
  const { user } = useUser()
  const { t, i18n } = useTranslation()
  const { native_language, setNativeLanguage } = useNeedsAnalysis()
  const { getToken } = useAuthToken()

  const handleLanguageChange = (language: NativeLanguage) => {
    setNativeLanguage(language)

    // Change i18n locale to match selected language
    const locale = LANGUAGE_TO_LOCALE[language]
    if (locale) {
      // Persist immediately so global sync effects (e.g. in Header) can't overwrite the user's selection.
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(I18NEXT_LANGUAGE_STORAGE_KEY, locale)
        } catch {
          // Ignore storage errors
        }
      }

      // Defer language change to allow the Select dropdown portal to close first.
      // Without this, i18n.changeLanguage triggers a re-render while the Radix portal
      // is still in the DOM, causing a React DOM conflict (removeChild error).
      requestAnimationFrame(() => {
        i18n.changeLanguage(locale).catch((error) => {
          console.error('Failed to change language:', error)
        })
      })
    }

    // Save to Clerk metadata for backward compatibility
    if (user) {
      user
        .update({
          unsafeMetadata: {
            ...user.unsafeMetadata,
            nativeLanguage: language,
            preferredLanguage: locale,
          },
        })
        .catch(() => {
          // Silently fail - data will still be saved to backend
        })
    }

    // Emails pick their locale from it.
    if (locale) {
      void getToken()
        .then((token) => (token ? meService.setPreferredLocale(token, locale) : undefined))
        .catch(() => {
          // Best-effort
        })
    }
  }
  return (
    <CarouselItem className='px-2 sm:px-8'>
      <div className='space-y-3 py-2 sm:space-y-6 sm:py-6'>
        <div className='space-y-1 sm:space-y-2'>
          <h3 id='preferred-language-heading' className='text-base font-medium text-gray-900 sm:text-xl'>
            {t('needsAnalysis.preferredLanguage.title')}
          </h3>
        </div>

        <div className='w-full max-w-md'>
          <Select value={native_language || undefined} onValueChange={handleLanguageChange}>
            <SelectTrigger aria-labelledby='preferred-language-heading' className='w-full'>
              <SelectValue placeholder={t('needsAnalysis.preferredLanguage.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {NATIVE_LANGUAGES.map((language) => (
                <SelectItem key={language} value={language}>
                  <span>{NATIVE_LANGUAGE_LABELS[language] ?? language}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </CarouselItem>
  )
}
